import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  JSX,
} from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { alpha, darken, lighten, useTheme } from "@mui/material";
import { AxiosError } from "axios";

// Tipos para o contexto de notificação
interface Notification {
  open: boolean;
  code: number | string | null;
  message?: string;
  severity: "success" | "error" | "warning" | "info";
  error?: AxiosError | null;
}

interface NotificationProviderProps {
  children: ReactNode;
}

type NotifyFunction = (
  code: number | string | null,
  message?: string,
  severity?: Notification["severity"],
  error?: AxiosError | null
) => void;

// Contexto para notificações
const NotificationContext = createContext<NotifyFunction | undefined>(
  undefined
);

export function NotificationProvider({
  children,
}: NotificationProviderProps): JSX.Element {
  const [notification, setNotification] = useState<Notification>({
    open: false,
    code: 0,
    message: "",
    severity: "info",
    error: null,
  });

  const theme = useTheme();
  const notify: NotifyFunction = (code, message, severity = "info", error) => {
    setNotification({ open: true, code, message, severity, error });
  };
  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };
  const customColors = {
    success: theme.palette.alert.success,
    info: theme.palette.alert.info,
    error: theme.palette.alert.error,
    warning: theme.palette.alert.warning,
  };
  return (
    <NotificationContext.Provider value={notify}>
      {children}
      <Snackbar
        open={notification.open}
        onClose={closeNotification}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity || "success"}
          variant={theme.palette.mode === "dark" ? "filled" : "standard"}
          sx={{
            backgroundColor: customColors[notification.severity || "success"],
          }}
        >
          {notification.code
            ? `${notification.code} - ${
                (notification.error?.response?.data as { message: string })
                  ?.message ||
                notification.error?.message ||
                notification.message
              }`
            : notification.error?.message || notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

// Hook para usar o contexto
export const useNotification = (): NotifyFunction => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification deve ser usado dentro de um NotificationProvider"
    );
  }
  return context;
};
